import QRCode from 'qrcodejs2'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { GET } from '@/utils/Request'
import { GET_EXAM_RESULT } from '@/const/api'
import BarcodeGenerator from '@/utils/BarcodeGenerator'


class ResultGenerator {
  constructor() {
    this.isGenerating = false
    this.container = null
  }

  async generateResult(unit, quota, onProgress = null, onError = null) {
  if (this.isGenerating) {
    throw new Error('result generation already in progress');
  }

  this.isGenerating = true;

  try {
    // Step 1: Fetch result data
    if (onProgress) onProgress('Fetching result data...', 10);
    const resultInfo = await this.fetchResult(unit, quota);
    //console.log('resultInfo',resultInfo)

    // Step 2: Create temporary container
    if (onProgress) onProgress('Preparing result...', 35);
    this.createContainer();

    // Step 3: Render result HTML
    if (onProgress) onProgress('Rendering result...', 50);
    // ✅ Updated QR text as requested
    const qrText = `${process.env.VUE_APP_FRONTEND_URL}check-exam-result/${resultInfo.examResult.hash_key}`;
    this.render(resultInfo);
    

    // Step 4: Generate QR code
    if (onProgress) onProgress('Generating QR code...', 60);
    await this.generateQRCodeInContainer(qrText);

    // Step 5: Wait for resources
    if (onProgress) onProgress('Loading fonts and images...', 70);
    await this.waitForResourcesLoaded();

    // Step 6: Generate PDF
    if (onProgress) onProgress('Generating PDF...', 85);
    await this.generatePDF(resultInfo.exam_roll, unit, resultInfo.quota);

    // Step 7: Cleanup
    if (onProgress) onProgress('Download complete!', 100);
    this.cleanup();

    this.isGenerating = false;
  } catch (error) {
    this.cleanup();
    this.isGenerating = false;
    if (onError) onError(error);
    throw error;
  }
}



async fetchResult(unit, quota) {
  try {
    const response = await GET(`${GET_EXAM_RESULT}${unit}/${quota}`);

    if (response.data.status !== 'success') {
      throw new Error(response.data.message || 'Failed to fetch result data');
    }

    const data = response.data; // because all fields are under data.data
    //console.log(data);
	return {
	      applicant: data.applicant,
	      application: data.application,
	      examResult: data.result,
	      examMarks: data.marks,
	      unit: data.result.unit,
	      quota: data.result.quota,
	      exam_roll: data.application.exam_roll.toString(),
	      application_id: data.application.application_id.toString(),
	      unit_co_ordinator: data.unit_co_ordinator,
	      co_ordinator_signature: data.co_ordinator_signature,
	      hash_key: data.hash_key
	    };

	  } catch (error) {
	    console.error(error);
	    throw error;
	  }
	}

	createContainer() {
    // Remove existing container if any
    this.cleanup()

    // Create new container
    // A4 dimensions at 96 DPI (standard web DPI)
    const a4Width = 794 // 210mm in pixels
    const a4Height = 1123 // 297mm in pixels

    this.container = document.createElement('div')
    this.container.id = 'result-generator'
    this.container.style.position = 'absolute'
    this.container.style.left = '-9999px'
    this.container.style.top = '-9999px'
    this.container.style.width = a4Width + 'px'
    this.container.style.height = a4Height + 'px'
    this.container.style.backgroundColor = 'white'
    this.container.style.boxSizing = 'border-box'

    document.body.appendChild(this.container)
  }

  render(resultInfo) {
  	//console.log(resultInfo);
    const { applicant, examResult, unit, quota, exam_roll, application_id,examMarks } = resultInfo
    const unitUpper = unit.toUpperCase()

    const html = `
<style>
@import url('https://fonts.googleapis.com/css2?family=Courier+Prime:ital,wght@0,400;0,700;1,400;1,700&family=Roboto+Condensed:ital,wght@0,100..900;1,100..900&family=Tiro+Bangla:ital@0;1&family=Victor+Mono:ital,wght@0,500;1,500&display=swap');

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-size: 12px;
  line-height: 1.3;
  color: black;
  background-color: white;
}

.bangla-text { font-family: "Tiro Bangla", serif; font-weight: 400; }
.bangla-text-bold { font-family: "Tiro Bangla", serif; font-weight: 500; }
.english-text { font-family: "Roboto Condensed", sans-serif; font-weight: 400; }
.english-text-bold { font-family: "Roboto Condensed", sans-serif; font-weight: 700; }

.container { width: 100%; padding: 10px 40px 10px 40px; margin: auto; }
.row { display: flex; flex-wrap: wrap; margin: 0; }
.col-md-2, .col-md-6, .col-md-8, .col-md-12 { padding: 0 5px; margin-bottom: 5px; }
.col-md-2 { flex: 0 0 16.6667%; max-width: 16.6667%; }
.col-md-6 { flex: 0 0 50%; max-width: 50%; }
.col-md-8 { flex: 0 0 66.6667%; max-width: 66.6667%; }
.col-md-12 { flex: 0 0 100%; max-width: 100%; }

.text-center { text-align: center; }
.text-left { text-align: left; }
.text-right { text-align: right; }
.font-weight-bold { font-weight: bold; }

.text-center-flex {
  justify-content: center;
}

.text-right-flex {
  justify-content: flex-end;
}

.university-name {
  font-size: 28px;
  line-height: 1.4;
  margin-bottom: 2px;
}
.admission-title { font-size: 12px; margin-bottom: 5px; }
.university-logo { max-width: 100%;height: auto;max-height: 105px; }

.roll-number-section {
  font-size: 14px;
  color: black;
}

.application-id-section {
  font-size: 14px;
  color: black;
  text-align: right;
}

.qrcode { width: 80px; height: 80px; margin: auto; }

.card { border: none; margin-bottom: 8px; padding: 0; background-color: transparent !important; }
.card-header {
  background-color: #343a40;
  color: #fff;
  font-weight: bold;
  padding: 5px 10px;
  font-size: 14px;
}
.card-body { padding: 5px 10px; background-color: transparent !important;}

.table th,
.table td {
  padding: 2px 4px; /* reduced from 3px-5px */
  border: none;     /* no border */
  text-align: left;
  font-size: 12px;  /* smaller font for compactness */
}

.table th {
  background-color: #f1f1f1;
  font-weight: bold;
}

.table-info td {
  background-color: #eef7fb;
}

.count-desc { font-family: "Roboto Condensed", sans-serif; font-weight: bold; }
.roll-digit { border: 1px solid black; padding: 1px 4px; font-size: 12px; display: inline-block; margin-right: 2px; }
.roll-digit-last { border-right: 1px solid black; }
span[style*="border: 2px solid black"] { display: inline-block; }

p { margin: 2px 0; }
.font-14{
	font-size:14px;
}
.font-12{
	font-size:13px;
}


.subject-choice-header {
  display: flex;
  flex-direction: column; /* stack items vertically */
  justify-content: center; /* center vertically (optional) */
  align-items: center;     /* center horizontally */
  gap: 5px; /* space between title and badge */
  margin-top: 2px;
  margin-bottom: 5px;
}

.unit-quota-badge {
  border: 1px solid black;
  border-radius: 5px;
  padding: 2px 6px;
  font-size: 12px;
  display: inline-block;
}

/* Container rows */
.subject-choice-row {
  display: flex;
  flex-wrap: nowrap;
  border-bottom: 1px solid #ddd;
  padding: 4px 0;
}

.subject-choice-row:last-child {
  border-bottom: none;
}

/* Header row */
.subject-choice-header-row {
  background-color: #eef7fb;
  font-weight: bold;
}

/* Cells */
.subject-choice-cell {
  padding: 2px 4px;
  font-size: 12px;
  display: flex;
  align-items: center;
}

/* Specific column widths */
.code-cell { flex: 0 0 20%; text-align: center; }
.title-cell { flex: 0 0 50%; text-align: left; }
.serial-cell { flex: 0 0 25%; text-align: center; }

.upper-case{
	text-transform: uppercase;
}
.subject-cell-title { flex: 0 0 56%; text-align: center; font-size:12px; }
.mark-cell-title-subject{ flex: 0 0 32%; text-align: center; font-size:12px;}
.mark-cell-title{ flex: 0 0 17%; text-align: center; font-size:12px;}
.mark-cell-subject { flex: 0 0 32%; text-align: center; font-size:14px;}
.mark-cell { flex: 0 0 17%; text-align: center; font-size:14px;}
.subject-cell { flex: 0 0 56%; text-align: center; font-size:14px; }



/* Regular row */
.ob-title-cell {
  flex: 0 0 56%; /* Exam Name */
  font-size:14px;
}

.obtain-cell {
  flex: 0 0 22%; /* GPA or Marks */
  font-size:14px;
}

/* Deduction/Total/Status/Merit row */
.deduct-cell {
  flex: 0 0 78%; /* Spans first 2 columns: 56% + 22% */
  font-size:14px;
}

.deduct-cell + .obtain-cell {
  flex: 0 0 22%; /* Matches Marks column width */
	font-size:14px;
}


/* Optional styling for card body */
.card-body {
  padding: 5px 10px;
}

.applicant-info-row {
  display: flex;
  flex-wrap: nowrap;
  margin-bottom: 4px;
  font-size: 12px;
}

.applicant-info-label {
  flex: 0 0 30%; /* adjust width of label column */
  font-weight: bold;
}

.applicant-info-separator {
  flex: 0 0 5%;  /* small space for ':' */
  text-align: center;
}

.applicant-info-value {
  flex: 1 0 65%; /* remaining width */
}


.result-generator-wrapper {
  position: relative;
  overflow: hidden;
  min-height: 297mm; /* A4 full height */
  /* Ensure this element has no explicit background-color itself if you remove z-index: -1 */
}

.result-generator-wrapper::before {
  content: '';
  position: absolute;
  top: 56%;
  left: 50%;
  width: 100%;
  height: 100%;
  background-image: url('https://cu-admission.sgp1.cdn.digitaloceanspaces.com/logo/cu_watermark.png');
  background-repeat: no-repeat;
  background-position: center center;
  background-size: 50%;
  /* *** Recommended Change 1: Set a much lower opacity for a true watermark effect *** */
  opacity: 0.50;
  transform: translate(-50%, -50%);
  pointer-events: none;
  /* *** Recommended Change 2: Remove z-index: -1 to avoid being hidden by a parent's background-color *** */
  /* z-index: -1; */
}


/* Highlighted result summary box */

.result-summary-header {
  padding: 20px 18px;     /* More space left-right, medium top-bottom */  
}
.result-summary-box {
  border: 2px solid #333;
  border-radius: 6px;
  padding: 20px 18px;     /* More space left-right, medium top-bottom */  
  background-color: transparent;
}

/* Each row inside box */
.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;         /* small top-bottom spacing */
  border-bottom: 1px solid #ddd;
}

.summary-row:last-child {
  border-bottom: none;    /* removes last line */
}

/* Label column */
.summary-label {
  flex: 1;
  font-size: 14px;        /* slightly larger */
  font-weight: 600;
}

/* Value column */
.summary-value {
  flex: 0 0 22%;
  text-align: center;
  font-size: 15px;        /* highlight bigger */
  font-weight: bold;
}



/* Force A4 page size for print */
@page { size: A4; margin: 15mm 10mm; }
</style>

<div class="result-generator-wrapper">
<section class="event team-sec team-grid speaker set-relative py-5" id="speaker">
  <div class="container">

    <!-- Header -->
    <div class="row">
      <div class="col-md-2 text-center">
        <img alt="Logo" class="university-logo" src="https://cu-admission.sgp1.digitaloceanspaces.com/logo/admit_logo.png">
      </div>
      <div class="col-md-8 text-center">
        <div class="bangla-text-bold university-name">চট্টগ্রাম বিশ্ববিদ্যালয়</div>
        <div class="bangla-text admission-title">১ম বর্ষ স্নাতক (সম্মান) ভর্তি পরীক্ষা, শিক্ষাবর্ষ: ২০২৬-২০২৭</div>
      </div>
      <div class="col-md-2 text-center">
        <div class="qrcode" id="qrCodeUrl"></div>
      </div>
    </div>

    <!-- Subject-Choice Info -->
    <div class="row">
      <div class="col-md-12 text-center subject-choice-header">
        <div class="english-text-bold" style="font-size: 16px; margin:0;">Admission Test Result</div>
        <div class="english-text unit-quota-badge">
          Unit: <b>${unitUpper}</b> &nbsp; Quota: <b>${quota}</b>
        </div>
      </div>
    </div>

    <!-- Roll & Application -->
    <div class="row">
      <div class="col-md-6">
        <div class="english-text roll-number-section">Admission Roll: <b>${this.renderDigits(exam_roll)}</b></div>
      </div>
      <div class="col-md-6 text-right">
        <div class="english-text application-id-section">Application ID: <b>${this.renderDigits(application_id)}</b></div>
      </div>
    </div>


    <!-- Applicant Info -->
    <div class="row">
      <div class="col-md-12">
        <div class="card">
          <div class="card-header english-text">Applicant's Info</div>
          

          <div class="card-body">

            <div class="applicant-info-row english-text">
              <div class="applicant-info-label font-12">Name</div>
              <div class="applicant-info-separator font-12">:</div>
              <div class="applicant-info-value english-text font-14">${applicant.name}</div>
            </div>

            <div class="applicant-info-row english-text">
              <div class="applicant-info-label font-12">Father's Name</div>
              <div class="applicant-info-separator font-12">:</div>
              <div class="applicant-info-value english-text font-14">${applicant.fname}</div>
            </div>

            <div class="applicant-info-row english-text">
              <div class="applicant-info-label font-12">Mother's Name</div>
              <div class="applicant-info-separator font-12">:</div>
              <div class="applicant-info-value english-text font-14">${applicant.mname}</div>
            </div>

          </div>



    </div>



     <div class="card mt-1">
          <div class="card-header english-text">Marks Obtained</div>
          <div class="card-body px-0">

            <!-- Table header -->
            <div class="subject-choice-row subject-choice-header-row english-text">
              <div class="subject-choice-cell mark-cell-title-subject upper-case text-center-flex">Subject</div>
              <div class="subject-choice-cell mark-cell-title upper-case text-center-flex">${unit === 'A' ? 'Number of Questions' : 'Total Marks'}</div>
              <div class="subject-choice-cell mark-cell-title upper-case text-center-flex">Gross Score</div> 
              <div class="subject-choice-cell mark-cell-title upper-case text-center-flex">Deductions</div>
              <div class="subject-choice-cell mark-cell-title upper-case text-center-flex">Net Score</div>            
            </div>

            <!-- Table rows -->
            ${examMarks.map(sub => `
            <div class="subject-choice-row">
              <div class="subject-choice-cell mark-cell-subject english-text text-center-flex">${sub.subject}</div>
              <div class="subject-choice-cell mark-cell english-text text-center-flex">${sub.max}</div>
              <div class="subject-choice-cell mark-cell english-text text-center-flex">${sub.correct}</div>
              <div class="subject-choice-cell mark-cell text-center-flex">${sub.deduct}</div>
              <div class="subject-choice-cell mark-cell text-center-flex">${sub.total}</div>
            </div>
            `).join('')}
            

          </div>
        </div>




      <div class="card mt-3">
		    <div class="px-5">
          <div class="card-header english-text">Admission Test Result Summary</div>
        </div>
		    <div class="card-body px-5 py-2">

		   
        
          <div class="result-summary-box">

              <!-- Obtain Mark 
              <div class="summary-row">
                  <div class="summary-label english-text">Obtained Marks</div>
                  <div class="summary-value english-text text-right">${examResult.exam_score ? examResult.exam_score:'0.00'.toFixed(2)}</div>
              </div>
              -->
              <!-- Deduction Row -->
              <div class="summary-row">
                  <div class="summary-label english-text">Deductions - Wrong Answers</div>
                  <div class="summary-value english-text text-right">${examResult.deduct}</div>
              </div>

              <!-- Deduction Row -->
              ${examResult.penalty && examResult.penalty > 0 ?
              `<div class="summary-row">
                  <div class="summary-label english-text">Deductions - Irregular</div>
                  <div class="summary-value english-text text-right">${examResult.penalty}</div>
              </div>`:''
              }

              <!-- Total Score Row -->
              <div class="summary-row">
                  <div class="summary-label english-text">Net Score</div>
                  <div class="summary-value english-text text-right">${examResult.total}</div>
              </div>

              <!-- Status Row -->
              <div class="summary-row">
                  <div class="summary-label english-text">Result</div>
                  <div class="summary-value english-text text-right">${examResult.status}</div>
              </div>

              <!-- Merit -->
              ${ examResult.merit ? `
              <div class="summary-row">
                  <div class="summary-label english-text">Merit Position</div>
                  <div class="summary-value english-text text-right">${examResult.merit}</div>
              </div>
              ` : ''}

          </div>



		    </div>
		  </div>








  </div>
</section>
</div>
`;


    
    this.container.innerHTML = html;


  }

  async generateQRCodeInContainer(qrText) {
    return new Promise((resolve) => {
      const qrCodeContainer = this.container.querySelector('#qrCodeUrl')

      if (qrCodeContainer) {
        // Clear any existing content
        qrCodeContainer.innerHTML = ''

        // Create canvas div for QR code
        const canvasDiv = document.createElement('div')
        canvasDiv.id = 'canvas'
        qrCodeContainer.appendChild(canvasDiv)

        // Generate QR code
        new QRCode('canvas', {
          text: qrText,
          width: 100,
          height: 100,
          colorDark: '#000000',
          colorLight: '#ffffff',
          correctLevel: QRCode.CorrectLevel.H
        })

        // Wait for QR code to render
        setTimeout(() => resolve(), 200)
      } else {
        resolve()
      }
    })
  }

  

  renderDigits(number) {
    const digits = String(number).split('')
    return digits.map((digit, index) => {
      const isLast = index === digits.length - 1
      return `<span class="roll-digit ${isLast ? 'roll-digit-last' : ''}">${digit}</span>`
    }).join('')
  }

  /**
   * Wait for all resources (fonts, images) to load
   */
  async waitForResourcesLoaded() {
    // Load font
    await document.fonts.load('16px "Tiro Bangla"')

    // Wait for all images to load
    const images = this.container.querySelectorAll('img')
    await Promise.all(
      Array.from(images).map((img) => {
        if (img.complete) {
          return Promise.resolve()
        }
        return new Promise((resolve, reject) => {
          img.onload = resolve
          img.onerror = () => {
            console.warn('Image failed to load:', img.src)
            resolve() // Resolve anyway to not block
          }
          // Timeout after 5 seconds
          setTimeout(resolve, 5000)
        })
      })
    )

    // Optimize images by reducing their display size
    //await this.optimizeImages()

    // Small delay to ensure everything is rendered
    await new Promise(resolve => setTimeout(resolve, 300))
  }

  async generatePDF(examRoll, unit, quota) {
  const element = this.container.querySelector('.result-generator-wrapper');

  // Render the whole content to a canvas
  const canvas = await html2canvas(element, {
    scale: 1.5,
    useCORS: true,
    backgroundColor: '#ffffff'
  });

  const imgData = canvas.toDataURL('image/jpeg', 0.85);
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();

  const imgProps = pdf.getImageProperties(imgData);
  const pdfImgHeight = (imgProps.height * pdfWidth) / imgProps.width;

  let position = 0;
  let remainingHeight = pdfImgHeight;

  // Add pages
  while (remainingHeight > 0) {
    pdf.addImage(imgData, 'JPEG', 0, position, pdfWidth, pdfImgHeight);
    remainingHeight -= pdfHeight;
    position -= pdfHeight;

    if (remainingHeight > 0) {
      pdf.addPage();
    }
  }

  // ----------- Add Footer to last page -----------
  pdf.setPage(pdf.getNumberOfPages()); // go to last page
  const footerText = 'Powered by ICT Cell, University of Chittagong';
  pdf.setFontSize(10);
  pdf.setFont('Roboto Condensed', 'italic');

  // Position footer text at bottom left (10mm from bottom)
  pdf.text(footerText, 15, pdfHeight - 10);

  // Generate and add barcode at bottom right
  try {
    const barcodeDataURL = BarcodeGenerator.generateBarcodeDataURL(examRoll, {
      width: 3,
      height: 40,
      format: 'CODE128'
    });
    
    // Position barcode at bottom right (10mm from right edge, 18mm from bottom)
    const barcodeWidth = 60; // Width in mm
    const barcodeHeight = 14; // Height in mm
    const barcodeX = pdfWidth - barcodeWidth - 10; // 10mm from right
    const barcodeY = pdfHeight - 18; // 18mm from bottom
    
    pdf.addImage(barcodeDataURL, 'PNG', barcodeX, barcodeY, barcodeWidth, barcodeHeight);
  } catch (error) {
    console.warn('Failed to generate barcode:', error);
    // Continue without barcode if generation fails
  }

  // Save PDF
  const filename = `cu_result_${examRoll}_${unit.toLowerCase()}_${quota.toLowerCase()}.pdf`;
  pdf.save(filename);
  //const pdfBlob = pdf.output('blob');
  //const pdfUrl = URL.createObjectURL(pdfBlob);

  // open outside your project
  //window.open(pdfUrl, "_blank");
}

cleanup() {
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container)
      this.container = null
    }
  }

}


export default new ResultGenerator()
